import React, {useEffect, useRef, useCallback, useState, forwardRef, useImperativeHandle} from 'react';
import {useAuth} from '../contexts/AuthContext';
import {useGlobalProvider} from "../contexts/GlobalProvider";
import {rules, tagSelectRules, viewerLinkModes, getViewerDefaults} from '../utilities/viewerUtils';



const ViewerRight = forwardRef(({urn, onOpenViewer}, ref) => {
    const viewerDivRight = useRef(null);
    const viewerRight = useRef(null);
    const {getAccessToken} = useAuth();
    const [initialized, setInitialized] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const {selectedTagIdViewerRight, setSelectedTagIdViewerLeft} = useGlobalProvider();

    // Get default configuration for right viewer
    const rightDefaults = getViewerDefaults('right');

    const [selectedOptions, setSelectedOptions] = useState(rightDefaults.viewerLinkModes);
    const selectedOptionsRef = useRef(selectedOptions);
    const [viewerLoaded, setViewerLoaded] = useState(false);

    // Initialize actions based on defaults
    const [actions, setActions] = useState({
        link2RightAndAsset: rightDefaults.viewerLinkModes.includes('link2RightAndAsset'),
        isolateSelection: rightDefaults.viewerLinkModes.includes('isolateSelection'),
        selectionColourRed: rightDefaults.viewerLinkModes.includes('selectionColourRed')
    });
    const [isProgrammaticIsolation, setIsProgrammaticIsolation] = useState(false);
    const isProgrammaticIsolationRef= useRef(isProgrammaticIsolation);

    const [selectedRule, setSelectedRule] = useState(rightDefaults.rules);
    // make sure the selected rule is current
    const selectedRuleRef = useRef(selectedRule);

    useEffect(() => {
        if (viewerRight.current && (selectedOptionsRef.current.includes('link2RightAndAsset'))&& selectedTagIdViewerRight) {
            console.log('Selected Tag right viewer:', selectedTagIdViewerRight);
            performSearchIsolateAndFit(selectedTagIdViewerRight);
        }
    }, [selectedTagIdViewerRight]);

    // Sync isProgrammaticIsolationRef with isProgrammaticIsolation
    useEffect(() => {
        isProgrammaticIsolationRef.current = isProgrammaticIsolation;
    }, [isProgrammaticIsolation]);


    useEffect(() => {
        selectedOptionsRef.current = selectedOptions;
    }, [selectedOptions]);

    useEffect(() => {
        if (!initialized || !viewerDivRight.current) return;

        const {Autodesk} = window;
        initializeViewer(Autodesk);
        const resizeObserver = new ResizeObserver(() => {
            resizeViewer();
        });

        resizeObserver.observe(viewerDivRight.current);

        return () => {
            if (viewerRight.current) {
                viewerRight.current.finish();
                viewerRight.current = null;
            }
            resizeObserver.disconnect();
        };
    }, [initialized]);


    // Simple useEffect to track URN prop changes
    useEffect(() => {
        console.log('ViewerRight: URN prop changed to:', urn);
    }, [urn]);

    useEffect(() => {
        if (viewerRight.current && urn) {
            const {Autodesk} = window;
            loadDocument(Autodesk, urn);
        }
    }, [urn, initialized]);




    const initializeViewer = (Autodesk) => {
        const options = {
            env: 'AutodeskProduction',
            // autodesk analytics
            useADP: false,
            getAccessToken: async (onTokenReady) => {
                let retryCount = 0;
                const maxRetries = 3;
                
                while (retryCount < maxRetries) {
                    try {
                        console.log(`[ViewerRight] Requesting access token (attempt ${retryCount + 1}/${maxRetries})...`);
                        const token = await getAccessToken();
                        console.log('[ViewerRight] Access token received:', token ? 'Yes' : 'No');
                        if (!token) {
                            throw new Error('No token received');
                        }
                        const timeInSeconds = 3600;
                        onTokenReady(token, timeInSeconds);
                        console.log('[ViewerRight] Token provided to viewer');
                        return; // Success, exit the retry loop
                    } catch (error) {
                        retryCount++;
                        console.error(`[ViewerRight] Error fetching access token (attempt ${retryCount}):`, error);
                        
                        if (retryCount < maxRetries) {
                            console.log(`[ViewerRight] Retrying in ${retryCount * 1000}ms...`);
                            await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
                        } else {
                            console.error('[ViewerRight] All token fetch attempts failed');
                        }
                    }
                }
            },
        };

        Autodesk.Viewing.Initializer(options, () => {
            const config = {
                // don't load extensions here to early.
                // extensions: ['Autodesk.Viewing.ZoomWindow'],
            };
            viewerRight.current = new Autodesk.Viewing.GuiViewer3D(viewerDivRight.current, config);
            viewerRight.current.start();

            loadDocument(Autodesk, urn);

            viewerRight.current.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, function (event) {
                if (!isProgrammaticIsolationRef.current && event.dbIdArray.length > 0) {
                    setViewerLoaded(true);
                    // cleaning the string from the MTEXT or ....
                    console.log('object clicked');
                    processDbId(viewerRight.current, event.dbIdArray[0]);
                }
            });
        });
    };

    const performSearchIsolateAndFit = (tagId) => {
        if (!viewerRight.current) return;

        setIsProgrammaticIsolation( true);
        console.log('Performing search isolate and fit isProgrammaticIsolationRef :', !isProgrammaticIsolationRef.current);

        viewerRight.current.search(tagId, (ids) => {

            if (selectedOptions.includes('isolateSelection')) {
                viewerRight.current.isolate(ids);
            }
            // important for the selection colour
            viewerRight.current.select(ids);

            if (ids.length > 0) {
                viewerRight.current.fitToView(ids);
//                 let is3D = viewerRight.current.model.is3d();
//                 let camera = viewerRight.current.getCamera();
//                 let originalFov = camera.fov;
//                 if (!is3D) {
// //                     // this is a 2D model
// //                     // camera.position.z -= 10  // Adjust this value to achieve the desired level of "zoom out"
// //                     // viewer.applyCamera(camera, true);
// //                     //camera.zoom = 2;
// //                     // viewerLeft.current.set2dSelectionColor(new THREE.Color(200,200,0),1); // Blue for 2D selection
// // // TODO play aroung with values
//                     camera.fov *= 0.8;
//                     // viewerRight.current.applyCamera(camera);
//                     viewerRight.current.impl.invalidate(true);
//                 }
//                 if (is3D) {
//                     // this is a 3D model
//                     // camera.position.z *= 2  // Adjust this value to achieve the desired level of "zoom out"
//                     // viewerRight.current.applyCamera(camera, true);
//                     //
//                     // camera.fov = 50;
//                     // viewerRight.current.applyCamera(camera, true);
//                     // // // redraw view
//                     // viewerRight.current.impl.invalidate(true);
//                 }
//                 // viewerRight.current.fitToView(ids)
//                 // // When done, restore original fov
//
//                 camera.fov = originalFov;
//                 viewerRight.current.applyCamera(camera, true);
//                 // Update the viewer again
//                 // viewerRight.current.impl.invalidate(true);

            }
            setIsProgrammaticIsolation(false);


        });
    };

    const handleRuleChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedRule(selectedValue);
        // console.log('Selected rule handleRuleChange :', selectedValue,selectedRule);
    };
    useEffect(() => {
        // console.log('Selected rule updated useeffect:', selectedRule);
        selectedRuleRef.current = selectedRule;
    }, [selectedRule]); // This effect runs every time selectedRule changes


    const processDbId = (viewer, dbId) => {
        viewer.getProperties(dbId, (props) => {
            console.log('Properties of the selected object processDbId viewer right:', props);
            console.log('Rule viewer right:', selectedRule);

            if (selectedOptionsRef.current.includes('isolateSelection')) {
                viewerRight.current.isolate(dbId);
            }

            // Add logging inside the tagSelectRules function to debug its behavior
            const extractedString = tagSelectRules(props, selectedRuleRef.current);

            if (extractedString) {
                console.log('Extracted string:', extractedString);
                setSelectedTagIdViewerLeft(extractedString);
            } else {
                console.warn('tagSelectRules did not return a valid string. Result:', extractedString);
            }
        }, (error) => {
            console.error('Error getting properties:', error);
        });
    };
    const loadDocument = (Autodesk, urn) => {
        console.log('Load urn:', urn);
        Autodesk.Viewing.Document.load(
            `urn:${urn}`,
            (doc) => {
                const viewable = doc.getRoot().getDefaultGeometry();
                viewerRight.current.loadDocumentNode(doc, viewable).then(() => {
                    // to get not get only shapes and cylinders
                    viewerRight.current.setSelectionMode(Autodesk.Viewing.SelectionMode.LAST_OBJECT)
                    viewerRight.current.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, function () {
                        console.log('Geometry loaded event fired.');
                        setViewerLoaded(true);
                        loadCustomExtensions();
                        performSearchIsolateAndFit(selectedTagIdViewerRight);
                    });
                });
            },
            (errorCode) => {
                console.error('Error loading document:', errorCode);
            }
        );
    };

    const resizeViewer = useCallback(() => {
        if (viewerRight.current) {
            viewerRight.current.resize();
        }
    }, []);



    const closeViewer = () => {
        setInitialized(false);
        const {Autodesk} = window;

        if (viewerRight.current) {
            viewerRight.current.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT);
            viewerRight.current.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT);
            viewerRight.current.finish();
            viewerRight.current = null;
            // setInitialized(false);
        }
    };

    const openViewer = async () => {
        try {
            setIsInitializing(true);
            console.log('[ViewerRight] Pre-fetching access token before viewer initialization...');
            // Pre-fetch the token to ensure it's available when the viewer needs it
            const token = await getAccessToken();
            if (!token) {
                console.error('[ViewerRight] Failed to get access token, cannot open viewer');
                setIsInitializing(false);
                return;
            }
            console.log('[ViewerRight] Token pre-fetched successfully, initializing viewer...');
            
            setInitialized(true);
            setIsInitializing(false);
            // Notify parent component that viewer is being opened
            if (onOpenViewer) {
                onOpenViewer();
            }
        } catch (error) {
            console.error('[ViewerRight] Failed to pre-fetch token:', error);
            setIsInitializing(false);
        }
    };

    // Expose openViewer and closeViewer functions to parent component via ref
    useImperativeHandle(ref, () => ({
        openViewer,
        closeViewer
    }));

    const handleSelectOptions = (event) => {
        const value = event.target.value;

        setSelectedOptions((prevSelected) =>
            prevSelected.includes(value)
                ? prevSelected.filter((mode) => mode !== value)
                : [...prevSelected, value]
        );

        setActions((prevActions) => ({
            ...prevActions,
            [value]: !prevActions[value]
        }));
    };
    useEffect(() => {
        console.log('Actions state updated viewer right:', actions);
    }, [actions]);

    useEffect(() => {
        console.log("viewer loaded",viewerLoaded)
        if (!viewerLoaded || !viewerRight.current) {
            console.warn('Viewer right not fully loaded yet.');
            return;
        }

        if (selectedOptionsRef.current.includes('selectionColourRed')) {
            viewerRight.current.setSelectionColor((0xff0000), window.Autodesk.Viewing.SelectionType.MIXED);
            viewerRight.current.set2dSelectionColor((0xff0000), 1);
            console.log("selection color set to red");
        } else {
            viewerRight.current.setSelectionColor((0x82B4FA), window.Autodesk.Viewing.SelectionType.MIXED);
            viewerRight.current.set2dSelectionColor((0x82B4FA), 1);
            console.log("selection color back to standard")
        }
    }, [selectedOptionsRef.current, viewerLoaded, actions]);
// load extensions after model is loaded
    function loadCustomExtensions() {
        const extensions = [
            "Autodesk.DocumentBrowser",   // First extension
            "Autodesk.Viewing.ZoomWindow" // Second extension
            // "ToolbarExtension"
        ];

        extensions.forEach(extensionName => {
            viewerRight.current.loadExtension(extensionName)
                .then(function (extensionInstance) {
                    console.log(`Extension loaded: ${extensionName}`, extensionInstance);
                })
                .catch(function (error) {
                    console.error(`Error loading extension: ${extensionName}`, error);
                });
        });
    }



    return (
        // set the width only if the viewer is open
        <div style={{width: '100%', height: initialized ? '400px' : '100%'}}>
            <div ref={viewerDivRight} id="viewerDivRight" style={{width: '100%', height: '100%'}}></div>
            <div className="viewerButtonsRight">
                {!initialized && !isInitializing &&
                    <button className="btn btn-secondary w-auto" onClick={openViewer}>Open right viewer</button>}
                {isInitializing &&
                    <button className="btn btn-secondary w-auto" disabled>Loading token...</button>}
                {initialized &&
                    <button className="btn btn-secondary w-auto" onClick={closeViewer}>Close right viewer</button>}
                <select id="rulesDropdown" value={selectedRule} onChange={handleRuleChange}>
                    {rules.map((rule) => (
                        <option key={rule.value} value={rule.value}>
                            {rule.label}
                        </option>
                    ))}
                </select>
                <select
                    id="viewerLinkModeDropdown"
                    // className="form-select"
                    value=""
                    onChange={handleSelectOptions}
                    style={{height: 'auto'}}
                >
                    {viewerLinkModes.map((mode) => (
                        <option
                            key={mode.value}
                            value={mode.value}
                            style={{
                                backgroundColor: selectedOptions.includes(mode.value) ? 'green' : 'white',
                                color: selectedOptions.includes(mode.value) ? 'white' : 'black'
                            }}
                        >
                            {mode.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
});

ViewerRight.displayName = 'ViewerRight';

export default ViewerRight;
